import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface CartItem {
  id: string;
  itemId: string;
  title: string;
  image: string;
  pricePerDay: number;
  startDate: Date | null;
  endDate: Date | null;
  ownerName: string;
  city: string;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (item: Omit<CartItem, 'id' | 'startDate' | 'endDate'>) => void;
  removeFromCart: (id: string) => void;
  updateCartItemDates: (id: string, startDate: Date | null, endDate: Date | null) => void;
  clearCart: () => void;
  getCartTotal: () => number;
  getCartItemCount: () => number;
  isInCart: (itemId: string) => boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    const saved = localStorage.getItem('rentify-cart');
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.map((item: any) => ({
        ...item,
        startDate: item.startDate ? new Date(item.startDate) : null,
        endDate: item.endDate ? new Date(item.endDate) : null,
      }));
    }
    return [];
  });

  useEffect(() => {
    localStorage.setItem('rentify-cart', JSON.stringify(items));
  }, [items]);

  const addToCart = (item: Omit<CartItem, 'id' | 'startDate' | 'endDate'>) => {
    const newItem: CartItem = {
      ...item,
      id: crypto.randomUUID(),
      startDate: null,
      endDate: null,
    };
    setItems((prev) => [...prev, newItem]);
  };

  const removeFromCart = (id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const updateCartItemDates = (id: string, startDate: Date | null, endDate: Date | null) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, startDate, endDate } : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const getCartTotal = () => {
    return items.reduce((total, item) => {
      if (item.startDate && item.endDate) {
        const days = Math.ceil(
          (item.endDate.getTime() - item.startDate.getTime()) / (1000 * 60 * 60 * 24)
        );
        return total + item.pricePerDay * days;
      }
      return total;
    }, 0);
  };

  const getCartItemCount = () => items.length;

  const isInCart = (itemId: string) => items.some((item) => item.itemId === itemId);

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        updateCartItemDates,
        clearCart,
        getCartTotal,
        getCartItemCount,
        isInCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
